/**
 * Public /resume-review/start — the shareable "get your CV polished" landing
 * page. Client enters name + email + optional discount code → we open a
 * $207 Stripe checkout → Stripe redirects to the confirmation page → they
 * click through to the CV Builder intake (existing flow from packages.py).
 *
 * Reads the `?src=` query param and forwards it as UTM-style tracking so
 * Cara/Laura can see which channel each buyer came through (LinkedIn,
 * Instagram, WhatsApp, referrals).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Sparkles, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PACKAGE_ID = "resume_review";
const PRICE_AUD = 207;
const PRICE_NZD = 227;  // matches NZD_BY_AUD in Landing.jsx

export default function ResumeReviewStart() {
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "" });
  const [discountCode, setDiscountCode] = useState("");
  const [showDiscount, setShowDiscount] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const sourceHint = useMemo(() => {
    const q = new URLSearchParams(window.location.search);
    return q.get("src") || q.get("source") || "";
  }, []);

  useEffect(() => {
    // Klaviyo onsite tracking removed (Feb 2026 migration to Resend).
    // If we ever want a "page viewed" event, wire it via /api/admin/email-hub
    // or a lightweight analytics endpoint here.
  }, [sourceHint]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.first_name) { toast.error("First name and email are required."); return; }
    setSubmitting(true);
    try {
      // Persist the source hint in sessionStorage so the confirmation page
      // (or a downstream migration) can pick it up if we ever want to.
      try { if (sourceHint) sessionStorage.setItem("rr_source_hint", sourceHint); } catch (err) { console.warn("ResumeReviewStart: sessionStorage unavailable", err); }

      const origin = window.location.origin;
      const r = await axios.post(`${API}/packages/checkout`, {
        package_id: PACKAGE_ID,
        email: form.email.trim().toLowerCase(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        origin_url: origin,
        discount_code: (discountCode.trim() || null),
      });
      if (!r.data?.url) throw new Error("No checkout URL returned.");
      // Ship them to Stripe.
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — email us at hello@theditchcollective.co.nz");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--paper)] flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.3em] text-[var(--ink)] font-bold bg-[var(--lime)] px-3 py-1.5 rounded-full">
            <Sparkles size={12} /> CV Builder
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[var(--ink)] mt-5 leading-tight">
            Get your CV<br/>Australian-ready.
          </h1>
          <p className="text-[var(--ocean)] mt-4 text-base leading-relaxed">
            Fill in the intake, upload your current CV — our team polishes it
            for the AU market and emails your final version back within{" "}
            <b>48 hours</b>.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-[var(--line)] p-7 space-y-4" data-testid="rr-start-form">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ink)] mb-1">First name <span className="text-red-500">*</span></span>
              <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                data-testid="rr-start-first-name"
                placeholder="Alex"
                className="w-full rounded-lg border border-[var(--line)] px-3 py-2.5 text-[15px] focus:border-[var(--ink)] outline-none" />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ink)] mb-1">Last name</span>
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                placeholder="Smith"
                className="w-full rounded-lg border border-[var(--line)] px-3 py-2.5 text-[15px] focus:border-[var(--ink)] outline-none" />
            </label>
          </div>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ink)] mb-1">Email <span className="text-red-500">*</span></span>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="rr-start-email"
              placeholder="alex@example.com"
              className="w-full rounded-lg border border-[var(--line)] px-3 py-2.5 text-[15px] focus:border-[var(--ink)] outline-none" />
          </label>

          {/* Discount code — collapsed by default so it doesn't look like the
              buyer's supposed to have one. */}
          {!showDiscount ? (
            <button type="button" onClick={() => setShowDiscount(true)}
              data-testid="rr-start-toggle-discount"
              className="text-xs text-[var(--ocean)] underline underline-offset-4 hover:text-[var(--ink)]">
              Have a discount code?
            </button>
          ) : (
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ink)] mb-1">Discount code</span>
              <input value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                data-testid="rr-start-discount"
                placeholder="LAURATESTFOUNTAIN182"
                className="w-full rounded-lg border border-[var(--line)] px-3 py-2.5 text-[15px] uppercase tracking-wide focus:border-[var(--ink)] outline-none" />
            </label>
          )}

          {/* Price + Stripe CTA */}
          <div className="pt-2">
            <div className="flex items-baseline justify-between mb-3">
              <span className="text-xs uppercase tracking-widest font-bold text-[var(--ocean)]">Today's total</span>
              <span className="font-display text-3xl font-extrabold text-[var(--ink)]">
                AU${PRICE_AUD}
                <span className="text-xs font-normal text-[var(--ocean)] ml-2">/ NZ${PRICE_NZD}</span>
              </span>
            </div>
            <button type="submit" disabled={submitting || !form.email || !form.first_name}
              data-testid="rr-start-submit"
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[var(--ink)] text-[var(--paper)] font-display font-bold hover:brightness-110 transition-all disabled:opacity-50">
              {submitting ? <Loader2 className="animate-spin" size={16} /> : <>Pay & open the CV Builder <ArrowRight size={16} /></>}
            </button>
            <p className="mt-3 text-[11px] text-[var(--ocean)] text-center inline-flex items-center gap-1.5 justify-center w-full">
              <Lock size={11} /> Secure checkout via Stripe. After payment you land straight on the CV intake form.
            </p>
          </div>
        </form>

        <p className="text-center text-xs text-[var(--ocean)] mt-6">
          Already paid? <a href="/resume-review/intake" className="underline">Open your intake link</a>.
        </p>
      </div>
    </div>
  );
}
